import { useId } from 'react';
import { trackProps } from '../../helpers/utils/analytics';
import { ChevronDown, LogOut, UserCheck } from 'lucide-react';
import { Box, Flex, Text, Stack } from '../ui';
import './watchingChip.css';
import { pressWash } from '../ui/press';

interface WatchingChipProps {
  isOpen: boolean;
  onToggle: () => void;
  onSwitchSeat: () => void;
  onLeave: () => void;
}

// Emerald/Header menu Menu=Watching (1598:20287). The tab wraps the chip
// instead of the 44pt hamburger slot. Always two rows, so the path is fixed.
const MENU_WIDTH = 200;
const MENU_HEIGHT = 153;

const WatchingMenuShape = () => {
  const gradientId = useId();
  const path = [
    'M12 42',
    'H75',
    'C79.418 42 83 38.418 83 34',
    'V12',
    'C83 5.373 88.373 0 95 0',
    'H188',
    'C194.627 0 200 5.373 200 12',
    'V139',
    'C200 145.627 194.627 151 188 151',
    'H12',
    'C5.373 151 0 145.627 0 139',
    'V54',
    'C0 47.373 5.373 42 12 42',
    'Z',
  ].join(' ');
  return (
    <svg
      aria-hidden="true"
      className="watching-menu-shape"
      width={MENU_WIDTH}
      height={151}
      viewBox="0 0 200 151"
      style={{
        position: 'absolute',
        top: 2,
        left: 0,
        overflow: 'visible',
        filter: 'drop-shadow(0 16px 40px rgba(0, 0, 0, 0.6))',
      }}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2="0"
          y2="151"
          gradientUnits="userSpaceOnUse"
        >
          <stop className="grad-top" />
          <stop className="grad-bottom" offset="1" />
        </linearGradient>
      </defs>
      <path
        d={path}
        fill={`url(#${gradientId})`}
        stroke="var(--spades-border-strong)"
        strokeWidth="1.5"
      />
    </svg>
  );
};

/**
 * Compact viewer status pill. Shows a live dot, "Watching" and a chevron so
 * it fits next to the title and hamburger. Tapping it opens the attached
 * tab (Switch seat, Leave).
 *
 * Controlled: the parent (Header) owns `isOpen` so opening this menu can close
 * the hamburger menu (and vice-versa) — the two must never be open at once or
 * one eclipses the other.
 */
const WatchingChip = ({
  isOpen,
  onToggle,
  onSwitchSeat,
  onLeave,
}: WatchingChipProps) => {
  return (
    <Box position="relative" h="44px" display="flex" alignItems="center" flexShrink={0}>
      <Flex
        data-testid="watching-chip"
        className="watching-chip"
        position="relative"
        zIndex={isOpen ? 120 : undefined}
        align="center"
        gap="calc(var(--spades-spacing-xs) + 2px)"
        bg={isOpen ? 'transparent' : 'var(--spades-surface)'}
        borderStyle="solid"
        borderColor={isOpen ? 'transparent' : 'var(--spades-border-strong)'}
        borderRadius="full"
        px="var(--spades-spacing-sm)"
        py="var(--spades-spacing-xs)"
        cursor="pointer"
        color="var(--spades-ivory)"
        css={pressWash}
        {...trackProps(
          isOpen ? 'watching_menu_closed' : 'watching_menu_opened',
        )}
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <Box
          data-testid="live-dot"
          position="relative"
          w="12px"
          h="12px"
          flexShrink={0}
        >
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            style={{
              position: 'absolute',
              left: -2,
              top: -2,
              overflow: 'visible',
            }}
          >
            <circle
              data-live-ping
              cx="8"
              cy="8"
              r="6"
              fill="var(--spades-error)"
              fillOpacity="0.35"
            />
            <circle cx="8" cy="8" r="6" fill="var(--spades-error)" fillOpacity="0.3" />
            <circle cx="8" cy="8" r="4" fill="var(--spades-error)" />
          </svg>
        </Box>
        <Text
          fontSize="var(--spades-font-size-xs)"
          fontWeight="700"
          lineHeight="1.25"
          color="var(--spades-ivory)"
          whiteSpace="nowrap"
        >
          Watching
        </Text>
        <Box
          data-testid="watching-chevron"
          display="flex"
          transform={isOpen ? 'rotate(180deg)' : undefined}
        >
          <ChevronDown size={14} aria-hidden="true" />
        </Box>
      </Flex>

      {isOpen && (
        <>
          <Box
            position="fixed"
            top="0"
            left="0"
            w="100vw"
            h="100vh"
            zIndex="100"
            {...trackProps(
              isOpen ? 'watching_menu_closed' : 'watching_menu_opened',
            )}
            onClick={onToggle}
          />
          <Box
            data-testid="watching-menu"
            position="absolute"
            top="0"
            right="0"
            w={`${MENU_WIDTH}px`}
            h={`${MENU_HEIGHT}px`}
            zIndex="110"
          >
            <WatchingMenuShape />
            <Stack
              gap={0}
              position="relative"
              pt="calc(var(--spades-spacing-xl) + var(--spades-spacing-ms))"
              pb="calc(var(--spades-spacing-xs) + 2px)"
            >
              <Flex
                data-testid="switch-seat-option"
                h="48px"
                px="var(--spades-spacing-md)"
                align="center"
                gap="var(--spades-spacing-ms)"
                cursor="pointer"
                fontSize="var(--spades-font-size-sm)"
                fontWeight="700"
                lineHeight="1.25"
                color="var(--spades-ivory)"
                _hover={{ bg: 'whiteAlpha.100' }}
                css={pressWash}
                onClick={onSwitchSeat}
              >
                <UserCheck size={20} aria-hidden="true" />
                <Text
                  fontSize="var(--spades-font-size-sm)"
                  fontWeight="700"
                  lineHeight="1.25"
                  color="var(--spades-ivory)"
                  whiteSpace="nowrap"
                >
                  Switch seat
                </Text>
              </Flex>
              <Box
                pl="calc(var(--spades-spacing-xl) + var(--spades-spacing-md))"
                pr="var(--spades-spacing-md)"
              >
                <Box h="1px" bg="var(--spades-border)" />
              </Box>
              <Flex
                data-testid="leave-option"
                h="48px"
                px="var(--spades-spacing-md)"
                align="center"
                gap="var(--spades-spacing-ms)"
                cursor="pointer"
                _hover={{ bg: 'whiteAlpha.100' }}
                css={pressWash}
                onClick={onLeave}
              >
                <LogOut size={20} aria-hidden="true" />
                <Text
                  fontSize="var(--spades-font-size-sm)"
                  fontWeight="700"
                  lineHeight="1.25"
                  color="var(--spades-ivory)"
                  whiteSpace="nowrap"
                >
                  Leave
                </Text>
              </Flex>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default WatchingChip;
